import { Button, Tooltip } from '@heroui/react';
import React, { useState } from 'react';
import { FiCheck, FiCopy, FiKey } from 'react-icons/fi';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useDailyCode } from '../hooks/useDailyCode';

/**
 * The code is almost always pasted straight into a login page, so hovering
 * shows it and clicking copies it: no popover to open and select by hand.
 */
export const DailyCodeButton: React.FC = () => {
  const dailyCode = useDailyCode();
  const { copy, justCopied } = useCopyToClipboard('code of the day');
  // Tooltips close on press. Reopen it so "Copied" is visible after a click.
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handlePress = () => {
    setIsTooltipOpen(true);
    copy(dailyCode);
  };

  return (
    <Tooltip
      isOpen={isTooltipOpen}
      onOpenChange={setIsTooltipOpen}
      placement="bottom-end"
      showArrow
      content={
        <div className="flex flex-col items-center gap-0.5 px-1 py-1">
          <span className="text-xs text-default-500">Code of the day</span>
          <span
            className="text-base font-bold tracking-wider text-foreground"
            data-testid="daily-code-value"
          >
            {dailyCode}
          </span>
          <span className="text-xs text-default-500">
            {justCopied ? 'Copied' : 'Click to copy'}
          </span>
        </div>
      }
    >
      <Button
        isIconOnly
        variant="light"
        size="sm"
        onPress={handlePress}
        aria-label="Copy code of the day"
        data-testid="daily-code-button"
        className="group text-primary-600 dark:text-primary-300 hover:bg-primary-100/60 dark:hover:bg-primary-400/20"
      >
        {/* Explicit size + inherited currentColor so the glyph stays
            visible on the header gradient in both themes */}
        {justCopied ? (
          <FiCheck size={18} className="text-success" />
        ) : (
          <>
            <FiKey
              size={18}
              className="group-hover:hidden group-data-[focus-visible=true]:hidden"
            />
            <FiCopy
              size={18}
              className="hidden group-hover:block group-data-[focus-visible=true]:block"
            />
          </>
        )}
      </Button>
    </Tooltip>
  );
};
